import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";

const emptyLine = () => ({ item_id: "", item_type: "", sku: "", name: "", quantity: 1, unit_price: 0, unit_cost: 0 });

export default function OrderFormDialog({ open, onOpenChange, onSaved }) {
  const { fmtMoney } = useCurrency();
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [customer_name, setCustomer] = useState("");
  const [customer_email, setEmail] = useState("");
  const [status, setStatus] = useState("completed");
  const [taxPct, setTaxPct] = useState(8);
  const [shipping, setShipping] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [lines, setLines] = useState([emptyLine()]);

  useEffect(() => {
    if (open) {
      Promise.all([api.get("/products"), api.get("/services", { params: { active_only: true } })])
        .then(([productResponse, serviceResponse]) => {
          setProducts(productResponse.data || []);
          setServices(serviceResponse.data || []);
        });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCustomer(""); setEmail(""); setStatus("completed"); setTaxPct(8);
      setShipping(0); setDiscount(0); setLines([emptyLine()]);
    }
  }, [open]);

  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines((p) => [...p, emptyLine()]);
  const removeLine = (i) => setLines((p) => p.filter((_, idx) => idx !== i));

  const pickOffering = (i, value) => {
    if (value === "__custom") return setLine(i, { item_id: "", item_type: "", sku: "", name: "", unit_price: 0, unit_cost: 0 });
    const [itemType, itemId] = value.split(":");
    const p = itemType === "service"
      ? services.find((x) => x.id === itemId)
      : products.find((x) => x.id === itemId);
    if (!p) return;
    setLine(i, {
      item_id: itemId,
      item_type: itemType,
      sku: p.sku || p.code,
      name: p.name,
      unit_price: p.price,
      unit_cost: p.cost,
    });
  };

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + (+l.quantity || 0) * (+l.unit_price || 0), 0),
    [lines]
  );
  const cogs = useMemo(
    () => lines.reduce((s, l) => s + (+l.quantity || 0) * (+l.unit_cost || 0), 0),
    [lines]
  );
  const tax = +(subtotal * (+taxPct / 100)).toFixed(2);
  const total = +(subtotal + tax + (+shipping || 0) - (+discount || 0)).toFixed(2);

  const submit = async () => {
    if (!customer_name) return toast.error("Customer name is required");
    if (lines.length === 0 || lines.every((l) => !l.name)) return toast.error("Add at least one line item");
    const valid = lines.filter((l) => l.name && +l.quantity > 0);
    const orderNumber = `#${Math.floor(20000 + Math.random() * 79999)}`;
    const payload = {
      order_number: orderNumber,
      customer_name,
      customer_email: customer_email || null,
      status,
      subtotal: +subtotal.toFixed(2),
      tax,
      shipping: +shipping || 0,
      discount: +discount || 0,
      total,
      cost_of_goods: +cogs.toFixed(2),
      lines: valid.map((l) => ({
        sku: l.sku, name: l.name,
        quantity: +l.quantity, unit_price: +l.unit_price, unit_cost: +l.unit_cost,
      })),
      channel: "manual",
    };
    try {
      await api.post("/orders", payload);
       toast.success(`Sale ${orderNumber} recorded`);
      onOpenChange(false);
      onSaved?.();
    } catch {
       toast.error("Failed to record sale");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-[760px]">
        <DialogHeader>
           <DialogTitle className="font-display">Record a sale</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Customer name *</Label>
              <Input data-testid="ord-input-customer" value={customer_name} onChange={(e) => setCustomer(e.target.value)} className="rounded-xl" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Email</Label>
              <Input data-testid="ord-input-email" value={customer_email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl" />
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="ord-input-status" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Tax %</Label>
              <Input data-testid="ord-input-tax" type="number" step="0.01" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
               <Label className="text-[12px]">Other fee</Label>
              <Input data-testid="ord-input-shipping" type="number" step="0.01" value={shipping} onChange={(e) => setShipping(e.target.value)} className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Discount $</Label>
              <Input data-testid="ord-input-discount" type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className="rounded-xl font-mono" />
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[12px] uppercase tracking-wider text-[#8E8E93]">Line items</Label>
              <Button data-testid="ord-add-line-btn" type="button" size="sm" variant="outline" onClick={addLine} className="rounded-full h-7 text-[11.5px]">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add line
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} data-testid={`ord-line-${i}`} className="grid grid-cols-12 gap-2 items-center bg-[#FAFAFA] rounded-2xl p-2.5 border border-black/[0.04]">
                  <div className="col-span-5">
                     <Select value={l.item_id ? `${l.item_type}:${l.item_id}` : "__custom"} onValueChange={(v) => pickOffering(i, v)}>
                       <SelectTrigger className="rounded-xl h-9 text-[13px] bg-white"><SelectValue placeholder="Choose service or product" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__custom">— Custom item —</SelectItem>
                         {services.map((service) => <SelectItem key={service.id} value={`service:${service.id}`}>Service · {service.name} ({service.code})</SelectItem>)}
                         {products.map((p) => <SelectItem key={p.id} value={`product:${p.id}`}>Retail · {p.name} ({p.sku})</SelectItem>)}
                      </SelectContent>
                    </Select>
                     {!l.item_id && (
                      <Input placeholder="Custom name" value={l.name} onChange={(e) => setLine(i, { name: e.target.value })} className="rounded-xl mt-1.5 h-9 text-[13px] bg-white" />
                    )}
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="1" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="rounded-xl h-9 text-[13px] font-mono bg-white" placeholder="Qty" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} className="rounded-xl h-9 text-[13px] font-mono bg-white" placeholder="Price" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" value={l.unit_cost} onChange={(e) => setLine(i, { unit_cost: e.target.value })} className="rounded-xl h-9 text-[13px] font-mono bg-white" placeholder="Cost" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button type="button" onClick={() => removeLine(i)} className="text-[#8E8E93] hover:text-[#FF3B30] p-1.5">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals preview */}
          <div className="rounded-2xl bg-[#F5F5F7] p-4 border border-black/[0.04]">
            {[
              ["Subtotal", subtotal],
              [`Tax (${taxPct}%)`, tax],
               ["Other fee", +shipping || 0],
              ["Discount", -(+discount || 0)],
              ["Total", total, true],
            ].map(([l, v, b], i) => (
              <div key={i} className={`flex justify-between text-[13px] py-1 ${b ? "border-t border-black/[0.06] mt-1 pt-2 font-semibold" : ""}`}>
                <span className={b ? "text-[#1C1C1E]" : "text-[#3C3C43]/80"}>{l}</span>
                <span className="font-mono">{fmtMoney(v)}</span>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="ord-save-btn" onClick={submit} className="rounded-full bg-black text-white hover:bg-black/85">
             Record sale
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
